import * as React from 'react';
import { SummaryProps } from './Summary.types';
import { BadgeImage } from '../BadgeImage';
import { CartButton } from '../CartButton';
import { Alert, AlertStatus } from '../Alert';
import { PriceSummary } from '../PriceSummary';
import { Swatch } from '../Swatch';
import './styles/Summary.css';
import { BadgeStyles, ShopifyProductVariant, SelectedOption } from '../../utils/interfaces';
import { Finish } from '../TitleBadge/TitleBadge.types';
import { Button } from '../Button';
import { Modal } from '../Modal';

export const Summary = ({
    children,
    selectedOptions,
    variants,
    title,
    quantity,
    properties,
    className,
    shineAssets,
}: SummaryProps) => {
    // State
    const [ showErrata, setShowErrata ] = React.useState(false);
    const [ showDelivery, setShowDelivery ] = React.useState(false);
    const tieredPricing = React.useRef();
    // Private
    const selectedStyle = selectedOptions ? selectedOptions.filter(option => option.name === 'Style') : [];
    const selectedColor = selectedOptions ? selectedOptions.filter(option => option.name === 'Colour') : [];
    const selectedVariant = variants ? variants.filter((variant: ShopifyProductVariant) => {
        return variant.option1 === selectedStyle[0].value && variant.option2 === selectedColor[0].value;
    }): null;
    const optionTitle = {name: 'Title',value: title};
    const optionQuantity = {name: 'Quantity',value: quantity};
    const optionFinish = {name: 'Finish',value: properties.finish};
    let options: SelectedOption[] = [];
    if(selectedOptions)
        options = [
            optionTitle,
            ...selectedOptions,
            optionFinish,
            optionQuantity,
        ];

    const renderStockMessage = () => {
        const hasSelectedVariant = selectedVariant.length > 0;
        if (!hasSelectedVariant) return null;

        const hasStock = selectedVariant[0].inventory_quantity > 0;
        const isCustomColour = selectedColor[0].value === 'Custom Colour';
        const exceedsStockLevel = quantity > selectedVariant[0].inventory_quantity;
        const isSilverFinish = properties.finish === Finish.Silver;

        if (hasStock && !isCustomColour && !exceedsStockLevel && !isSilverFinish) {
            return (
                <Alert status={AlertStatus.Success}>
                    {selectedVariant[0].inventory_quantity} available.
                </Alert>
            );
        }

        return (
            <Alert
                status={AlertStatus.Error}
            >
                3 week delivery
            </Alert>
        );
    }
    // Render
    return (
        <div className={`summary ${className}`}>
            <div className={`summary__panel summary__product-image`}>
                {selectedStyle.length > 0 ? (
                    <BadgeImage 
                        style={selectedStyle[0].value as BadgeStyles} 
                        color={properties.customColour ? properties.customColour : selectedColor[0].value as BadgeStyles}
                        title={title}
                        finish={properties.finish as Finish}
                        shineAssets={shineAssets}
                    />
                ) : null }  
            </div>
            <div className={`summary__panel`}>
                <PriceSummary price={selectedVariant[0] && selectedVariant[0].price}>
                    <div 
                        className="hulkapps-volumes" 
                        dangerouslySetInnerHTML={{ 
                            __html: selectedOptions && 
                                document.querySelector('#tiered-pricing')?.innerHTML
                        }} 
                    />
                </PriceSummary>
            </div>
            <div className={`summary__panel summary__selection`}>
                <h3>Your selection</h3>
                {renderStockMessage()}
                <ul className={`summary__selected-options`}>
                    {options ? options.map((option: SelectedOption, index: number) => (
                        <li className={`summary__selected-option`} key={`SummarySelectedOption${index}`}>
                            <div className={`summary__selected-option__name`}>
                                {index+1}. {option.name}
                            </div>
                            <div className={`summary__selected-option__value`}>
                                <Swatch 
                                    value={
                                        option.value === 'Custom Colour' ?
                                            properties.customColour :
                                            option.value as BadgeStyles
                                    } 
                                />
                                <span>
                                    {
                                        option.value === 'Custom Colour' ?
                                            properties.customColour :
                                            option.value ? 
                                                option.value : 
                                                'Not Selected'
                                    }
                                </span>
                            </div>
                        </li>
                    )) : null}
                </ul>
                <CartButton
                    productId={selectedVariant[0].id}
                    quantity={quantity}
                    properties={{
                        ...properties,
                    }}
                />
                <div
                    className="hulkapps-volumes"
                    id="tiered-pricing"
                    ref={tieredPricing}
                />
                
                {children}
            </div>

            <Button
                onClick={() => setShowErrata(true)}
                className="summary__modal-trigger"
                secondary
            >
                Important information 
                <span className="summary__modal-trigger__chevron">&gt;</span>
            </Button>
            <Button
                onClick={() => setShowDelivery(true)}
                className="summary__modal-trigger"
                secondary
            >
                Delivery information
                <span className="summary__modal-trigger__chevron">&gt;</span>
            </Button>
            <div className="summary__panel">
                <small>
                    The image above is an artistic representation of the badge.
                    The text layout is for illustration only and the text size and line breaks may vary slightly on the delivered badge.
                    Because of variations in screen colour calibrations, colours on the finished badge can vary slightly to what is displayed.
                </small>
            </div>
            <Modal
                show={showErrata}
                onChange={(show: boolean) => setShowErrata(show)}
            >
                <h1>Important information</h1>
                <p>Our metal Title Badge School Badges are of the highest quality, which promotes a desire for ownership that influences behaviour and encourages pupils to strive to achieve their potential.<br />
                By awarding these badges to pupils, you are giving them school badges that will be treasured forever and that will evoke fond memories when 'discovered' long after school badges days are over.<br />
                They are finished in actual gold or silver (nickle) plate and have a protective frontal dome that sets them off beautifully.</p>

                <p>
                    <strong>Material:</strong> Metal<br />
                    <strong>Finish:</strong> Gold plated (ask us for silver)<br />
                    <strong>Sizes:</strong> As shown on the style images<br />
                    <strong>Fitting:</strong> Brooch pin as standard (ask for other fittings)<br />
                    <strong>Surface:</strong> Clear epoxy dome cover over background colour
                </p>
            </Modal>
            <Modal
                show={showDelivery}
                onChange={(show: boolean) => setShowDelivery(show)}
            >
                <h1>Delivery</h1>
                <h4>DELIVERY TIMESCALE</h4>
                <p><strong>In stock</strong> Within 24 hours<br />
                <strong>Not in stock</strong> Within three weeks</p>
                <h4>STOCK</h4>
                <p>The quantity of stock that is shown on our web site is always ‘subject to availability’<br />
                This is because our available product stock changes very rapidly as we are fulfilling orders from our many customers.<br />
                The stock position is therefore intended to be indicative and is not a guarantee of availability.<br />
                This is in common with stock availability that is shown on most web sites.</p>
            </Modal>
        </div>
    );
};